import { appBarHeight } from '@/component/AppBar/AppBar';
import { Icon } from '@/icon/Icon';
import { List } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode, useState } from 'react';
import { useRail } from '../Drawer/useRail';
import { ListItemButton } from './ListItemButton';
import { ListItemContent } from './ListItemContent';
import { MainItemContainer } from './MainItemContainer';
import { NavItem } from './types';
import { useNavigation } from './useNavigation';

interface NavigationProps {
  navigation: NavItem[];
}

export const Navigation = ({ navigation }: NavigationProps) => {
  const [isRail] = useRail();
  const [isOver, setIsOver] = useState(false);

  return (
    <>
      <List
        disablePadding
        onMouseOver={() => {
          if (isRail) {
            setIsOver(true);
          }
        }}
      >
        <Content navigation={navigation} showExpand={!isRail} />
      </List>
      <RailOverlay onClose={() => setIsOver(false)} isVisible={isOver}>
        <Content navigation={navigation} showExpand />
      </RailOverlay>
    </>
  );
};

interface OverlayPaperProps {
  isVisible: boolean;
}
const OverlayPaper = styled('div', {
  name: 'Navigation',
  slot: 'OverlayPaper',
  shouldForwardProp: (propName) => propName !== 'isVisible',
})<OverlayPaperProps>(({ theme, isVisible }) => ({
  position: 'fixed',
  top: appBarHeight,
  left: 0,
  minWidth: 250,
  zIndex: 1200,
  height: `calc(100vh - ${appBarHeight}px)`,
  overflow: 'auto',
  opacity: 0,
  pointerEvents: 'none',
  borderRadius: 0,
  boxShadow: theme.shadows[4],
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create(['opacity'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
  ...(isVisible && {
    opacity: 1,
    pointerEvents: 'auto',
  }),
}));

interface RailOverlayProps {
  children: ReactNode;
  isVisible: boolean;
  onClose: () => void;
}

const RailOverlay = ({ isVisible, onClose, children }: RailOverlayProps) => {
  return (
    <OverlayPaper
      onMouseLeave={() => {
        onClose();
      }}
      isVisible={isVisible}
      children={children}
    />
  );
};

interface ContentProps {
  navigation: NavItem[];
  showExpand: boolean;
}

const Content = ({ navigation: allNavItems, showExpand }: ContentProps) => {
  const { navigation, setNewMainRoute } = useNavigation(allNavItems);

  return (
    <>
      {navigation.map((item, i) => {
        const mainItemActive = item.active;

        return (
          <MainItemContainer key={i} active={mainItemActive}>
            <ListItemButton
              to={item.to}
              mainItemActive={mainItemActive}
              selectable={!Boolean(item.subItems?.length)}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setNewMainRoute(item.to);
              }}
            >
              <ListItemContent label={item.label} icon={item.icon} />
              {!mainItemActive && showExpand && <Icon name="expand_more" />}
            </ListItemButton>
            {mainItemActive && item.subItems && (
              <List disablePadding>
                {item.subItems.map((subItem, i) => (
                  <ListItemButton
                    key={i}
                    to={subItem.to}
                    mainItemActive={mainItemActive}
                    selectable
                  >
                    <ListItemContent
                      label={subItem.label}
                      icon={subItem.icon}
                      isSubItem
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </MainItemContainer>
        );
      })}
    </>
  );
};
